/**
 * Schema describing which fields/related entities can be picked when building a
 * dynamic report. The labels here are identifiers (no accents): they travel to the backend and act as
 * the "key" used everywhere in the wizard (state.fields, filters, table columns). What the user sees is
 * their translation, under reportBuilder.field.<key> / reportBuilder.group.<key> (see reportLabelKey).
 */

export interface OneNSchema {
	summary: string[];
	detail: string[];
}

export interface EntitySchema {
	label: string;
	icon: string;
	groups: { [groupName: string]: string[] };
	oneN: { [subEntityName: string]: OneNSchema };
}

export type ReportEntityKey = 'vehicle' | 'conductor';

export const reportSchema: { [key in ReportEntityKey]: EntitySchema } = {
	vehicle: {
		label: 'Vehiculo',
		icon: 'pi pi-car',
		groups: {
			'Datos vehiculo': [
				'Matricula', 'Marca', 'Modelo', 'Version', 'Num Bastidor', 'Estado', 'Uso', 'Activo/Baja', 'Es Renting',
				'Afecta Nomina', 'Sustituye a', 'Fecha alta', 'Fecha Baja', 'Observaciones',
			],
			'Organizacion': [
				'Empresa', 'Delegacion', 'Subdelegacion', 'CECO', 'Responsable Flota', 'Responsable Servicio', 'Jefe de Zona',
				'Gestor', 'IVA', 'Fecha asignacion',
			],
			'Conductor': [
				'Codigo Conductor', 'Nombre Conductor', 'Apellidos Conductor', 'NIF Conductor', 'Empresa Conductor',
				'Division Conductor', 'Subdivision Conductor', 'CECO Conductor', 'Fecha asignacion conductor',
			],
			'Financiacion': [
				'Fin Proveedor', 'Fin Tipo Financiacion', 'Fin Num Contrato', 'Fin Tipo de interes', 'Fin Fecha Inicio',
				'Fin Fecha Fin', 'Fin Tipo de contrato', 'Fin Entrada en vigor', 'Fin Km Contrato', 'Fin Cuota Mensual',
				'Fin Coste de inversion', 'Fin Coste de exceso', 'Fin Coste por defecto', 'Fin Valor PFF', 'Fin Valor de opciones',
				'Fin Valor de extras', 'Fin Valor base imponible', 'Fin IVA financiacion', 'Fin Valor IVA', 'Fin Valor Total',
				'Fin Sustituido por', 'Fin Valor de recompra', 'Fin Ajuste km', 'Fin Forma de pago', 'Fin Tipo de renting',
				'Fin Observaciones financiacion', 'Fin Estado financiacion', 'Fin Fecha de cancelacion', 'Fin Km cancelacion',
				'Fin Valor de cancelacion',
			],
			'Seguro': [
				'Seg Aseguradora', 'Seg Num Poliza', 'Seg Agente', 'Seg Fecha inicio poliza', 'Seg Fecha fin poliza',
				'Seg Importe Seguro', 'Seg Importe flota', 'Seg Tipo Seguro', 'Seg Estado seguro', 'Seg Observaciones seguro',
				'Seg Fecha cancelacion seguro', 'Seg Fecha inicio recibo', 'Seg Fecha fin recibo', 'Seg Importe recibo',
				'Seg Importe flota recibo',
			],
		},
		oneN: {
			'Financiacion / Renting': {
				summary: ['Num contratos renting', 'Cuota mensual total'],
				detail: [
					'Proveedor', 'Tipo Financiacion', 'Num Contrato', 'Tipo de interes', 'Fecha Inicio', 'Fecha Fin', 'Tipo de contrato',
					'Entrada en vigor', 'Km Contrato', 'Cuota Mensual', 'Coste de inversion', 'Coste de exceso', 'Coste por defecto',
					'Valor PFF', 'Valor de opciones', 'Valor de extras', 'Valor base imponible', 'IVA financiacion', 'Valor IVA',
					'Valor Total', 'Sustituido por', 'Valor de recompra', 'Ajuste km', 'Forma de pago', 'Tipo de renting',
					'Observaciones financiacion', 'Estado financiacion', 'Fecha de cancelacion', 'Km cancelacion', 'Valor de cancelacion',
				],
			},
			'Seguros': {
				summary: ['Num polizas', 'Prima anual total'],
				detail: [
					'Aseguradora', 'Num Poliza', 'Agente', 'Fecha inicio poliza', 'Fecha fin poliza', 'Importe Seguro', 'Importe flota',
					'Tipo Seguro', 'Estado seguro', 'Observaciones seguro', 'Fecha cancelacion seguro', 'Fecha inicio recibo',
					'Fecha fin recibo', 'Importe recibo', 'Importe flota recibo',
				],
			},
			'Tarjetas combustible': {
				summary: ['Num tarjetas'],
				detail: [
					'Num tarjeta', 'Proveedor tarjeta', 'Estado tarjeta', 'Limite de producto', 'Limite operacion',
					'Fecha asignacion tarjeta', 'Fecha caducidad tarjeta', 'Fecha baja tarjeta',
				],
			},
			'Consumos': {
				summary: ['Total litros', 'Importe total consumos', 'Ultimo consumo'],
				detail: [
					'Num tarjeta consumo', 'Importe consumo', 'Fecha Consumo', 'Tipo consumo', 'Litros', 'Km consumo', 'Num factura',
					'Fecha factura', 'Establecimiento', 'Poblacion',
				],
			},
			'Impuestos': {
				summary: ['Num impuestos', 'Importe total impuestos'],
				detail: [
					'Descripcion impuesto', 'Importe impuesto', 'Fecha impuesto',
				],
			},
			'Multas': {
				summary: ['Num multas', 'Importe total multas'],
				detail: [
					'Conductor infractor', 'Importe Multa', 'Importe bonificacion', 'Num Expediente', 'Motivo', 'Emisor', 'Fecha Sancion',
					'Estado Expediente', 'Gestor', 'Fecha de notificacion', 'Metodo de notificacion',
					'Fecha de solicitud de identificacion', 'Fecha de identificacion', 'Metodo de identificacion', 'Pagado por adelantado',
					'Fecha pagado por adelantado', 'Observaciones multa',
				],
			},
			'Mantenimientos': {
				summary: ['Num mantenimientos', 'Ultima revision'],
				detail: [
					'Tipo Mantenimiento', 'Fecha ultimo mantenimiento', 'Fecha proximo mantenimiento', 'Fecha Realizado', 'Incidencias',
					'Observaciones mantenimiento',
				],
			},
			'Kilometros': {
				summary: ['Km actuales', 'Fecha ultimo registro km'],
				detail: [
					'Fecha lectura km', 'Km', 'Tipo km',
				],
			},
			'Accesorios': {
				summary: ['Num accesorios'],
				detail: [
					'Tipo Accesorio', 'Descripcion accesorio', 'Numero de serie', 'Fecha Instalacion', 'Fecha de vencimiento', 'Valor',
					'Verificado', 'Fecha verificado', 'Deshabilitado', 'Observaciones accesorio',
				],
			},
			'Documentos': {
				summary: ['Num documentos'],
				detail: [
					'Tipo Documento', 'Nombre Documento', 'Fecha documento',
				],
			},
			'Notas': {
				summary: ['Num notas', 'Ultima nota'],
				detail: [
					'Fecha Nota', 'Texto nota', 'Autor nota',
				],
			},
			'Siniestros': {
				summary: ['Num siniestros'],
				detail: [
					'Conductor detalle', 'Referencia', 'Fecha Siniestro', 'Expediente cerrado', 'Direccion siniestro',
					'Codigo postal siniestro', 'Poblacion siniestro', 'Provincia', 'Responsable flota siniestro', 'Tipo Siniestro',
					'Atestado', 'Num atestado', 'Culpabilidad', 'Alcohol/drogas', 'Sancion conductor', 'Siniestro total',
					'Asistencia sanitaria', 'Fecha peritacion', 'Version siniestro', 'Danos asegurado', 'Taller', 'Fecha Inicio Taller',
					'Fecha Fin Taller', 'Vehiculo alquiler', 'Fecha recogida veh alquiler', 'Fecha devolucion veh alquiler',
					'Fin reparacion',
				],
			},
			'Averias': {
				summary: ['Num averias'],
				detail: [
					'Conductor detalle', 'Tipo de renting', 'Empresa averia', 'Delegacion averia', 'Motivo Averia', 'Averia antieconomica',
					'Negligencia conductor', 'Averia cerrada', 'Taller', 'Fecha Inicio Taller', 'Fecha Fin Taller',
				],
			},
		},
	},
	conductor: {
		label: 'Conductor',
		icon: 'pi pi-user',
		groups: {
			'Datos conductor': [
				'Codigo Empleado', 'Nombre', 'Apellidos', 'NIF', 'Fecha nacimiento', 'Direccion', 'Codigo postal', 'Poblacion',
				'Telefono', 'Extension', 'Movil', 'Email empresa', 'Email personal',
			],
			'Documentacion personal': [
				'Fecha Cad. DNI', 'Fecha expedicion DNI',
			],
			'Organizacion': [
				'Empresa', 'CECO', 'Division', 'Subdivision',
			],
		},
		oneN: {
			'Carnets de conducir': {
				summary: ['Num carnets', 'Puntos DGT actuales'],
				detail: [
					'Tipo Carnet', 'Fecha Expedicion', 'Fecha Caducidad', 'Puntos DGT',
				],
			},
			'Vehiculos asignados': {
				summary: ['Num vehiculos asignados'],
				detail: [
					'Matricula', 'Marca', 'Modelo', 'Version', 'Fecha asignacion vehiculo', 'Fecha desasignacion',
				],
			},
			'Documentos': {
				summary: ['Num documentos'],
				detail: [
					'Tipo Documento', 'Nombre Documento', 'Fecha documento',
				],
			},
			'Incidencias conductor': {
				summary: ['Num incidencias', 'Importe total incidencias', 'Puntos retirados total'],
				detail: [
					'Matricula', 'Motivo', 'Fecha', 'Emisor', 'Responsable', 'Importe', 'Puntos', 'Descuento nomina',
					'Importe a descontar',
				],
			},
			'Multas': {
				summary: ['Num multas', 'Importe total multas'],
				detail: [
					'Matricula', 'Importe Multa', 'Importe bonificacion', 'Num Expediente', 'Motivo', 'Emisor', 'Fecha Sancion',
					'Estado Expediente', 'Gestor', 'Fecha de notificacion', 'Metodo de notificacion',
					'Fecha de solicitud de identificacion', 'Fecha de identificacion', 'Metodo de identificacion', 'Pagado por adelantado',
					'Fecha pagado por adelantado', 'Observaciones multa',
				],
			},
			'Siniestros': {
				summary: ['Num siniestros'],
				detail: [
					'Matricula', 'Referencia', 'Fecha Siniestro', 'Expediente cerrado', 'Direccion siniestro', 'Codigo postal siniestro',
					'Poblacion siniestro', 'Provincia', 'Responsable flota siniestro', 'Tipo Siniestro', 'Atestado', 'Num atestado',
					'Culpabilidad', 'Alcohol/drogas', 'Sancion conductor', 'Siniestro total', 'Asistencia sanitaria', 'Fecha peritacion',
					'Version siniestro', 'Danos asegurado', 'Taller', 'Fecha Inicio Taller', 'Fecha Fin Taller', 'Vehiculo alquiler',
					'Fecha recogida veh alquiler', 'Fecha devolucion veh alquiler', 'Fin reparacion',
				],
			},
			'Averias': {
				summary: ['Num averias'],
				detail: [
					'Matricula', 'Tipo de renting', 'Empresa averia', 'Delegacion averia', 'Motivo Averia', 'Averia antieconomica',
					'Negligencia conductor', 'Averia cerrada', 'Taller', 'Fecha Inicio Taller', 'Fecha Fin Taller',
				],
			},
		},
	},
};

export interface ReportFilter {
	field: string;
	op: '=' | '!=' | '>=' | '<=' | 'contiene';
	value: string;
}

export interface OneNMode {
	[subEntityName: string]: 'summary' | 'detail';
}

export interface SavedReport {
	id?: number;
	name: string;
	entity: ReportEntityKey;
	fields: string[];
	filters: ReportFilter[];
	oneNModes: OneNMode;
	detailEntity: string;
}

/** Date fields whose label does not contain the word "fecha". */
const dateFieldsWithoutFecha = ['Ultimo consumo', 'Ultima revision', 'Ultima nota', 'Entrada en vigor', 'Fin Entrada en vigor'];

export function isDateField(field: string): boolean {
	return field.toLowerCase().indexOf('fecha') >= 0 || dateFieldsWithoutFecha.indexOf(field) >= 0;
}

/** Fields the backend returns as "Si" / "No"; they are filtered with a Yes/No dropdown instead of free text. */
export const booleanFields = ['Activo/Baja', 'Es Renting', 'Afecta Nomina', 'Expediente cerrado', 'Averia cerrada', 'Averia antieconomica', 'Negligencia conductor', 'Siniestro total', 'Pagado por adelantado', 'Verificado', 'Deshabilitado', 'Atestado', 'Alcohol/drogas', 'Asistencia sanitaria', 'Sancion conductor', 'Incidencias', 'Descuento nomina'];

export function isBooleanField(field: string): boolean {
	return booleanFields.indexOf(field) >= 0;
}

/**
 * Stable, accent-free key of a schema label (field, group or sub-entity name), used to look its translation
 * up under reportBuilder.field.<key> / reportBuilder.group.<key> in the i18n files. The labels themselves stay
 * the identifiers sent to the backend; only what is displayed is translated.
 */
export function reportLabelKey(label: string): string {
	return (label || '')
		.normalize('NFD')
		.replace(/\p{M}/gu, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
}
